import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { fetchTenantSwitchSettings } from '@/api/tenantApi';
import {
  FetchTenantSwitchSettingsResponse,
  SwitchTargetTenant,
} from '@/types/api/tenantApi';

type UseFetchTenantSwitchSettingsParams = {
  m_affiliate_id: number;
  enabled?: boolean;
};

export const useFetchTenantSwitchSettings = ({
  m_affiliate_id,
  enabled = true,
}: UseFetchTenantSwitchSettingsParams) => {
  const [data, setData] = useState<FetchTenantSwitchSettingsResponse['data']>();
  const [tenantSwitchEnabled, setTenantSwitchEnabled] = useState(false);
  const [switchTargets, setSwitchTargets] = useState<SwitchTargetTenant[]>([]);
  const [error, setError] = useState<AxiosError>();
  const [isLoading, setIsLoading] = useState(false);
  const [loadedAffiliateId, setLoadedAffiliateId] = useState<number | null>(
    null,
  );
  const [retryCount, setRetryCount] = useState(0);
  const [errorAffiliateId, setErrorAffiliateId] = useState<number | null>(null);

  useEffect(() => {
    // 法人が変わった時点で旧法人の候補を隠す。0 はログアウト・顧客切替中。
    setData(undefined);
    setTenantSwitchEnabled(false);
    setSwitchTargets([]);
    setError(undefined);
    setErrorAffiliateId(null);
    setLoadedAffiliateId(null);
    if (!enabled || !m_affiliate_id) {
      setIsLoading(false);
      return;
    }

    let isCurrent = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await fetchTenantSwitchSettings({ m_affiliate_id });
        if (!isCurrent) return;
        const responseData = res.data;

        setData(responseData);
        setTenantSwitchEnabled(responseData.tenant_switch_enabled ?? false);
        setSwitchTargets(responseData.switch_targets ?? []);
        setLoadedAffiliateId(m_affiliate_id);
      } catch (err) {
        if (!isCurrent) return;
        setError(err as AxiosError);
        setErrorAffiliateId(m_affiliate_id);
        setTenantSwitchEnabled(false);
        setSwitchTargets([]);
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      isCurrent = false;
    };
  }, [enabled, m_affiliate_id, retryCount]);

  // useEffect の後処理前にも、旧法人の候補を新法人の画面に表示しない。
  const isCurrentAffiliate =
    enabled && m_affiliate_id > 0 && loadedAffiliateId === m_affiliate_id;

  return {
    data: isCurrentAffiliate ? data : undefined,
    error: enabled && errorAffiliateId === m_affiliate_id ? error : undefined,
    isLoading,
    tenantSwitchEnabled: isCurrentAffiliate && tenantSwitchEnabled,
    switchTargets: isCurrentAffiliate ? switchTargets : [],
    retry: () => setRetryCount((count) => count + 1),
  };
};
